
import {
  requestMediaPermissions
} from 'mic-check';

import { isSafari } from './utils';

const SILENT_RMS = 0.02
const LOW_RMS = 0.06
const SILENT_MS = 1200
const CHECK_SETTLE_MS = 200
const CHECK_WARMUP_MS = 700
const SPEECH_HOLD_MS = 200
const CONFIRMED_HOLD_MS = 1200
const UNHEARD_AFTER_MS = 2500
export const MIC_BAR_COUNT = 8

async function requestMedia() {
  let result = [false, null];
  await requestMediaPermissions().then(() => {
    result = [true, null];
  }).catch((err) => {
    console.error(err);
    result = [false, err];
  })
  return result;
}

function preferredConstraints() {
  const portrait = typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(orientation: portrait)').matches

  return {
    audio: { echoCancellation: false },
    video: {
      facingMode: 'user',
      width: { ideal: portrait ? 720 : 1280 },
      height: { ideal: portrait ? 1280 : 720 },
    }
  }
}

async function initRecording() {
  const attempts = [
    preferredConstraints(),
    { audio: true, video: { facingMode: 'user' } },
    { audio: true, video: true },
  ]

  let lastError = null

  for (const constraints of attempts) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      return [true, stream]
    } catch (e) {
      console.error(e)
      lastError = e
    }
  }

  return [false, lastError]
};

function recorderMimeType() {
  if (typeof MediaRecorder === 'undefined') {
    return { mimeType: '' }
  }

  if (isSafari()) {
    return { mimeType: 'video/mp4' }
  }

  const mimeTypes = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ]

  const supported = mimeTypes.find((mimeType) => MediaRecorder.isTypeSupported(mimeType))
  return { mimeType: supported || '' }
}

function audioTrackState(stream) {
  const track = stream && stream.getAudioTracks ? stream.getAudioTracks()[0] : null
  if (!track) {
    return { hasAudioTrack: false, muted: false, ended: true }
  }

  return {
    hasAudioTrack: track.readyState === 'live',
    muted: !!track.muted || track.enabled === false,
    ended: track.readyState === 'ended',
  }
}

function classifyMicLevel(rms, { hasAudioTrack = true, muted = false, ended = false, silentSince = null, now = Date.now() } = {}) {
  if (!hasAudioTrack || muted || ended) {
    return { state: 'silent', silentSince: silentSince || now }
  }

  if (rms < SILENT_RMS) {
    const started = silentSince || now
    return {
      state: (now - started) >= SILENT_MS ? 'silent' : 'low',
      silentSince: started,
    }
  }

  return {
    state: rms < LOW_RMS ? 'low' : 'good',
    silentSince: null,
  }
}

function micLabel(state, { hasAudioTrack = true, muted = false } = {}) {
  if (!hasAudioTrack) return 'No microphone connected'
  if (muted) return 'Microphone is muted'
  if (state === 'silent') return "We can't hear your mic"
  return 'Checking your mic…'
}

function litBarCount(rms) {
  if (rms <= 0) return 0
  return Math.min(MIC_BAR_COUNT, Math.max(1, Math.round(rms * 18)))
}

function isAudible(rms, { hasAudioTrack = true, muted = false, ended = false } = {}) {
  return hasAudioTrack && !muted && !ended && rms >= SILENT_RMS
}

function confirmSilentTake() {
  return window.confirm("We didn't detect any microphone audio. Use this video anyway?")
}

// One-time check: learn the quiet level, then wait for a normal voice above it.
// Room noise used to dismiss the banner before it could be seen.
function createMicCheck() {
  const startedAt = Date.now()
  const quietSamples = []
  let floor = null
  let speechSince = null
  let confirmedAt = null

  const update = (rms, track, now = Date.now()) => {
    if (!track.hasAudioTrack) {
      return { hide: false, label: 'No microphone connected' }
    }
    if (track.muted) {
      return { hide: false, label: 'Microphone is muted' }
    }

    const elapsed = now - startedAt
    if (elapsed >= CHECK_SETTLE_MS && elapsed < CHECK_WARMUP_MS) {
      quietSamples.push(rms)
    } else if (floor === null && elapsed >= CHECK_WARMUP_MS) {
      if (!quietSamples.length) {
        floor = 0
      } else {
        const sorted = quietSamples.slice().sort((a, b) => a - b)
        floor = sorted[Math.floor(sorted.length * 0.3)]
      }
    }

    const speechLine = Math.max(0.018, (floor || 0) + 0.012)

    if (!confirmedAt && elapsed >= CHECK_WARMUP_MS && rms >= speechLine) {
      speechSince = speechSince || now
      if ((now - speechSince) >= SPEECH_HOLD_MS) {
        confirmedAt = now
      }
    } else if (!confirmedAt) {
      speechSince = null
    }

    if (confirmedAt) {
      return {
        hide: (now - confirmedAt) >= CONFIRMED_HOLD_MS,
        label: 'Mic is on',
      }
    }

    if (elapsed >= UNHEARD_AFTER_MS) {
      return { hide: false, label: "We can't hear your mic" }
    }

    return { hide: false, label: 'Checking your mic…' }
  }

  return { update }
}

function bindMicMeter(root, level) {
  if (!root) return

  const label = root.querySelector('.mic-meter__label')
  const bars = root.querySelectorAll('.mic-meter__bar')
  const lit = litBarCount(level.rms)

  root.classList.toggle('is-good', level.state === 'good')
  root.classList.toggle('is-low', level.state === 'low')
  root.classList.toggle('is-silent', level.state === 'silent')

  if (label) label.textContent = micLabel(level.state, level)
  bars.forEach((bar, index) => {
    bar.classList.toggle('is-on', index < lit)
  })
}

function createMicMonitor(stream, onLevel) {
  const emit = (partial) => {
    const track = audioTrackState(stream)
    onLevel({ rms: 0, state: 'silent', ...track, ...partial })
  }

  if (!stream || !stream.getAudioTracks || stream.getAudioTracks().length === 0) {
    emit({ hasAudioTrack: false, ended: true })
    return { stop() {}, resume() {} }
  }

  const AudioContextClass = window.AudioContext || window.webkitAudioContext
  if (!AudioContextClass) {
    emit({ hasAudioTrack: true })
    return { stop() {}, resume() {} }
  }

  const ctx = new AudioContextClass()
  const source = ctx.createMediaStreamSource(stream)
  const analyser = ctx.createAnalyser()
  analyser.fftSize = 512
  analyser.smoothingTimeConstant = 0.7
  source.connect(analyser)

  const data = new Uint8Array(analyser.fftSize)
  const track = stream.getAudioTracks()[0]
  let raf = null
  let stopped = false
  let silentSince = Date.now()

  const tick = () => {
    if (stopped) return

    analyser.getByteTimeDomainData(data)
    let sum = 0
    for (let i = 0; i < data.length; i += 1) {
      const value = (data[i] - 128) / 128
      sum += value * value
    }
    const rms = Math.sqrt(sum / data.length)
    const trackState = audioTrackState(stream)
    const classified = classifyMicLevel(rms, {
      ...trackState,
      silentSince,
      now: Date.now(),
    })
    silentSince = classified.silentSince

    onLevel({
      rms,
      state: classified.state,
      ...trackState,
    })

    raf = window.requestAnimationFrame(tick)
  }

  const onTrackChange = () => {
    if (stopped) return
    const trackState = audioTrackState(stream)
    if (!trackState.hasAudioTrack || trackState.muted || trackState.ended) {
      silentSince = silentSince || Date.now()
      onLevel({ rms: 0, state: 'silent', ...trackState })
    }
  }

  if (track) {
    track.addEventListener('mute', onTrackChange)
    track.addEventListener('unmute', onTrackChange)
    track.addEventListener('ended', onTrackChange)
  }

  tick()

  return {
    stop() {
      stopped = true
      if (raf) window.cancelAnimationFrame(raf)
      if (track) {
        track.removeEventListener('mute', onTrackChange)
        track.removeEventListener('unmute', onTrackChange)
        track.removeEventListener('ended', onTrackChange)
      }
      try { source.disconnect() } catch (e) { /* already disconnected */ }
      try { analyser.disconnect() } catch (e) { /* already disconnected */ }
      if (ctx.state !== 'closed') {
        ctx.close().catch(() => {})
      }
    },
    resume() {
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {})
      }
    }
  }
}

export {
  requestMedia,
  initRecording,
  recorderMimeType,
  createMicMonitor,
  bindMicMeter,
  micLabel,
  litBarCount,
  isAudible,
  confirmSilentTake,
  createMicCheck,
  audioTrackState,
};
